import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiPackage, FiPrinter, FiLink
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import LinkedDocNumbers from '../../components/LinkedDocNumbers';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getTransactions,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import { getItemStockCached, invalidateItemStock, getAvailableSerials } from '../../utils/stockFlowHelper';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getVendorsCached, getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import VendorSelect from '../../components/VendorSelect';
import { useVendorPricing, getVendorPrice } from '../../utils/vendorPricingHelper';
import ItemSelect from '../../components/ItemSelect';
import { isFutureDate } from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ParentDocLink from '../../components/ParentDocLink';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { inActiveBranch } from '../../utils/branchLink';

const TRAN_TYPE_CODE = 'GRN';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Received', 'Invoiced', 'Cancelled']
};

const EMPTY_LINE = {
  itemId: '', itemCode: '', itemName: '',
  poLineId: '', description: '', unitOfMeasure: '', unitCode: '',
  orderedQty: 0, receivedQty: 0,
  unitCost: 0, lineTotal: 0,
  trackingType: 'None', batchNumber: '', expiryDate: '', serialNumbers: [],
  // Size fields (module item_has_size) — mirrors PurchaseInvoice.
  hasSize: false, sizeUnit: 'inches', length: 0, height: 0, thickness: 0,
  pieces: 0, sqftPerPiece: 0, totalSqft: 0, sizeLabel: ''
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Received: colors.success.main,
  Invoiced: '#9C27B0',
  Cancelled: colors.error.main,
};

// ── PO receiving progress (FLW-02) ──────────────────────────────────────────
// A GRN counts on its PO while it is Received or Invoiced. Each GRN line keeps
// the PO line it came from (poLineId = that line's number on the PO); older
// lines without it fall back to the same item. Progress is written straight
// onto the PO — only lines / status / updatedAt / updatedBy, the receiving
// fields the security rules let a GRN user change on a PO.
const GRN_COUNTS_ON_PO = ['Received', 'Invoiced'];
const PO_PROGRESS_STATUSES = ['Approved', 'Partially Received', 'Completed'];
const r6 = (n) => Math.round((Number(n) || 0) * 1e6) / 1e6;
const poLineNo = (pl, idx) => Number(pl?.lineNumber) || (idx + 1);

const findPOLineIndex = (poLines, ref, itemId, qty, removing) => {
  const n = Number(ref) || 0;
  if (n > 0) {
    const i = poLines.findIndex((pl, idx) => poLineNo(pl, idx) === n);
    if (i >= 0 && (!itemId || !poLines[i].itemId || poLines[i].itemId === itemId)) return i;
  }
  if (!itemId) return -1;
  const same = [];
  poLines.forEach((pl, idx) => { if (pl.itemId === itemId) same.push(idx); });
  const fit = same.find(idx => (removing
    ? (Number(poLines[idx].receivedQty) || 0) >= qty
    : (Number(poLines[idx].quantity) || 0) - (Number(poLines[idx].receivedQty) || 0) > 0));
  return fit !== undefined ? fit : (same.length ? same[0] : -1);
};

// GRN lines → { ref, itemId, qty } entries for the PO.
export const grnPOEntries = (lines) => (lines || []).map(l => ({
  ref: l.poLineId, itemId: l.itemId || '', qty: Number(l.acceptedQty ?? l.receivedQty) || 0
}));

// Adds (sign 1) or takes off (sign -1) received quantities on a copy of the PO lines.
export const shiftPOReceived = (poLines, entries, sign) => {
  const lines = (poLines || []).map(l => ({ ...l }));
  (entries || []).forEach(({ ref, itemId, qty }) => {
    if (!(qty > 0)) return;
    const i = findPOLineIndex(lines, ref, itemId, qty, sign < 0);
    if (i < 0) return;
    const rec = Math.max(0, r6((Number(lines[i].receivedQty) || 0) + sign * qty));
    lines[i] = { ...lines[i], receivedQty: rec, pendingQty: r6((Number(lines[i].quantity) || 0) - rec) };
  });
  return lines;
};

// Approved (nothing received) / Partially Received / Completed (all received).
// A PO in any other status (Draft, Cancelled) keeps it.
export const poStatusFromLines = (lines, current) => {
  if (!PO_PROGRESS_STATUSES.includes(current)) return current;
  const all = lines.length > 0 && lines.every(l => (Number(l.receivedQty) || 0) >= (Number(l.quantity) || 0));
  const some = lines.some(l => (Number(l.receivedQty) || 0) > 0);
  return all ? 'Completed' : some ? 'Partially Received' : 'Approved';
};

// Applies [{ entries, sign }] to the PO in one write (1 read + 1 write).
export const applyPOProgress = async (companyId, poId, changes, userId) => {
  const ref = doc(db, 'companies', companyId, 'transactions', poId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().tranTypeCode !== 'PO') return null;
  const po = snap.data();
  let lines = po.lines || [];
  (changes || []).forEach(({ entries, sign }) => { lines = shiftPOReceived(lines, entries, sign); });
  const upd = { lines, updatedAt: new Date().toISOString(), ...(userId ? { updatedBy: userId } : {}) };
  const status = poStatusFromLines(lines, po.status);
  if (status !== po.status) upd.status = status;
  await updateDoc(ref, upd);
  return { ...po, ...upd };
};

const GRN = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'grn');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: grns,
    loading,
    refresh: loadGRNs,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingGRN, setEditingGRN] = useState(null);
  const [viewingGRN, setViewingGRN] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', parentId: '', parentDocNumber: '',
    invoiceNo: '', vehicleNo: '', driverName: '',
    locationId: '', locationName: '',
    notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [approvedPOs, setApprovedPOs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stockBalances, setStockBalances] = useState({});

  // GRN list now comes from useDocListPagination above (loadGRNs = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): vendors/items come from the device-local
  // master cache (getMaster* → IndexedDB, 0 reads per open with a throttled
  // background change-check) instead of a full getDocs on every open. Each
  // screen keeps its own client-side filter/sort on the returned rows.
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations([...rows].sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '')));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to PO" dropdown — nothing on the list view uses it. It used to run on
  // every page open AND after every save, scanning full document history (all
  // Approved + all Partially Received POs ever). It now loads ONCE, lazily, the
  // first time a form is actually opened. List browsing and saving cost nothing.
  const formDataLoadedRef = useRef(false);

  // Load approved POs from unified transactions collection
  const loadApprovedPOs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Get POs with status Approved or Partially Received
      const approvedTxns = await getTransactions(activeCompany.id, {
        tranTypeCode: 'PO',
        status: 'Approved'
      });
      const partialTxns = await getTransactions(activeCompany.id, {
        tranTypeCode: 'PO',
        status: 'Partially Received'
      });
      setApprovedPOs([...approvedTxns, ...partialTxns]);
    } catch (error) { console.error('Error loading POs:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadApprovedPOs()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadApprovedPOs]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the GRN list itself loads inside useDocListPagination — not here.
  // D3: the PO loader is NOT here anymore — it loads lazily on first form open
  // (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadVendors(); loadItems(); loadLocations();
    loadEmployees(); loadJobs();
  }, [loadVendors, loadItems, loadLocations, loadEmployees, loadJobs]);

  // Filter
  const filteredGRNs = useMemo(() => {
    let filtered = grns;
    if (statusFilter !== 'All') filtered = filtered.filter(g => g.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(g =>
        g.docNumber?.toLowerCase().includes(q) ||
        g.partyName?.toLowerCase().includes(q) ||
        g.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [grns, statusFilter, searchQuery, sort]);

  // Calculate line
  const calculateLine = (line) => ({
    ...line,
    acceptedQty: line.receivedQty || 0, // receivedQty is accepted by default
    lineTotal: Math.round((line.receivedQty || 0) * (line.unitCost || 0) * 100) / 100
  });

  // Totals
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    return {
      totalReceived: lines.reduce((s, l) => s + (l.receivedQty || 0), 0),
      totalAmount: lines.reduce((s, l) => s + l.lineTotal, 0)
    };
  }, [lineItems]);

  // Form handlers
  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData(prev => ({ ...prev, partyId: vendorId, partyName: vendor?.vendorName || '', parentId: '', parentDocNumber: '' }));
    setLineItems([{ ...EMPTY_LINE }]);
  };

  const handlePOChange = (poId) => {
    const po = approvedPOs.find(p => p.id === poId);
    if (!po) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '' }));
      return;
    }
    setFormData(prev => ({
      ...prev, parentId: poId, parentDocNumber: po.docNumber,
      partyId: po.partyId, partyName: po.partyName
    }));
    // Auto-fill lines from PO (each line keeps the PO line it came from)
    const poLines = (po.lines || []).map((l, idx) => ({ ...l, _poLineNo: poLineNo(l, idx) }))
      .filter(l => (l.pendingQty ?? (l.quantity - (l.receivedQty || 0))) > 0);
    if (poLines.length > 0) {
      setLineItems(poLines.map(l => {
        const item = items.find(i => i.id === l.itemId);
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          poLineId: String(l._poLineNo),
          description: l.description || l.itemName || '',
          unitOfMeasure: l.unitOfMeasure || '',
          orderedQty: l.quantity || 0,
          receivedQty: l.pendingQty ?? (l.quantity - (l.receivedQty || 0)),
          unitCost: l.unitPrice || 0,
          trackingType: item?.trackingType || 'None',
          // Serial numbers kept on the order (imported from Excel) fill in here
          // for serial-tracked items.
          ...(item?.trackingType === 'Serial' && Array.isArray(l.serialNumbers) && l.serialNumbers.length
            ? { serialNumbers: [...l.serialNumbers] } : {})
        };
      }));
      // Part of the order already received: leave out serials already in stock.
      poLines.filter(l => Number(l.receivedQty) > 0 && Array.isArray(l.serialNumbers) && l.serialNumbers.length
          && items.find(i => i.id === l.itemId)?.trackingType === 'Serial')
        .forEach(l => {
          getAvailableSerials(activeCompany.id, l.itemId).then(inStock => {
            const have = new Set(inStock || []);
            setLineItems(prev => prev.map(x => (x.poLineId === String(l._poLineNo) && Array.isArray(x.serialNumbers))
              ? { ...x, serialNumbers: x.serialNumbers.filter(s => !have.has(s)) } : x));
          }).catch(() => { /* keep the order's list; the save check flags any already in stock */ });
        });
    }
  };

  const handleLocationChange = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    setFormData(prev => ({ ...prev, locationId, locationName: loc?.locationName || '' }));
  };

  // Vendor-wise Items & Pricing (module purchases_vendor_pricing): the picked
  // vendor's price list. Empty — and nothing is read — while the module is off.
  const vendorPricing = useVendorPricing(activeCompany?.id, formData.partyId, isModuleEnabled);

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].expiryDate = '';
          updated[index].serialNumbers = [];
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, unitCost: seeded.unitCost };
          // Vendor's own price wins over the item's purchase price.
          const vp = getVendorPrice(vendorPricing, value, item, updated[index].unitCode);
          if (vp !== null) updated[index].unitCost = vp;
          // Size mode (module item_has_size): seed dimensions, lock UoM to SQft,
          // and drive receivedQty (the stock quantity) from the computed Total SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
            updated[index].receivedQty = updated[index].totalSqft;
          } else {
            updated[index].hasSize = false;
          }
        }
      }

      if (field === 'unitCode' && updated[index].itemId && !updated[index].hasSize) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, unitCost: patch.unitCost };
          const vp = getVendorPrice(vendorPricing, updated[index].itemId, item, value);
          if (vp !== null) updated[index].unitCost = vp;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].receivedQty);
          if (twoQ !== null) updated[index].receivedQty = twoQ;
        }
      }

      // Size-mode edits: recompute Total SQft when L/H/T/Pieces/sizeUnit change and
      // keep receivedQty in sync with the computed SQft (stock + accounting use it).
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
        updated[index].receivedQty = updated[index].totalSqft;
      }

      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.receivedQty) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', parentId: '', parentDocNumber: '',
      invoiceNo: '', vehicleNo: '', driverName: '',
      locationId: '', locationName: '', notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingGRN(null);
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (grn) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load PO link data on demand
    setEditingGRN(grn);
    setFormData({
      docDate: grn.docDate || new Date().toISOString().split('T')[0],
      partyId: grn.partyId || '', partyName: grn.partyName || '',
      parentId: grn.parentId || '', parentDocNumber: grn.parentDocNumber || '',
      invoiceNo: grn.invoiceNo || '', vehicleNo: grn.vehicleNo || '',
      driverName: grn.driverName || '', locationId: grn.locationId || '',
      locationName: grn.locationName || '', notes: grn.notes || '', status: grn.status || 'Draft',
      employeeId: grn.employeeId || '', employeeName: grn.employeeName || '',
      jobId: grn.jobId || '', jobName: grn.jobName || ''
    });
    setLineItems(grn.lines?.length > 0 ? grn.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (grn) => { setViewingGRN(grn); setViewMode('view'); };

  // Handle navigation from reports (e.g., clicking a GRN document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingGRN(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Save GRN using transactionHelper
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.partyId) { alert('Please select a vendor'); return; }
    if (!formData.locationId && locations.length > 0) { alert('Please select a receiving location'); return; }
    if (!activeFinancialYear?.id) { alert('Please select an active financial year'); return; }

    const validLines = lineItems.filter(l => l.description && l.receivedQty > 0);
    if (validLines.length === 0) { alert('Please add at least one line with received quantity'); return; }

    // Validate batch/serial tracking
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch number is required for "${line.itemName || line.description}"`);
        return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const expectedQty = line.receivedQty || 0;
        if (serials.length !== expectedQty) {
          alert(`"${line.itemName || line.description}" requires exactly ${expectedQty} serial number(s). You entered ${serials.length}.`);
          return;
        }
        const uniqueSerials = new Set(serials);
        if (uniqueSerials.size !== serials.length) {
          alert(`Duplicate serial numbers found for "${line.itemName || line.description}"`);
          return;
        }
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const calc = calculateLine(l);
        return {
          lineNumber: i + 1,
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description,
          poLineId: l.poLineId || '',
          orderedQty: l.orderedQty || 0,
          receivedQty: l.receivedQty || 0,
          acceptedQty: l.receivedQty || 0, // receivedQty = acceptedQty
          unitOfMeasure: l.unitOfMeasure || '',
          unitCost: l.unitCost || 0,
          unitPrice: l.unitCost || 0, // For stockFlow
          quantity: l.hasSize ? (l.totalSqft || 0) : (l.receivedQty || 0), // For stockFlow (SQft for size items)
          lineTotal: calc.lineTotal || 0,
          // Batch / Serial tracking — MUST be saved so the Purchase Invoice (which
          // reads grn.lines[].batchNumber) and stockFlow carry the batch forward.
          trackingType: l.trackingType || 'None',
          batchNumber: l.batchNumber || '',
          expiryDate: l.expiryDate || null,
          serialNumbers: l.serialNumbers || [],
          // Size fields (module item_has_size) — persisted so prints & reload render correctly.
          hasSize: l.hasSize || false,
          sizeUnit: l.sizeUnit || '',
          length: Number(l.length) || 0,
          height: Number(l.height) || 0,
          thickness: Number(l.thickness) || 0,
          pieces: Number(l.pieces) || 0,
          sqftPerPiece: Number(l.sqftPerPiece) || 0,
          totalSqft: Number(l.totalSqft) || 0,
          sizeLabel: l.sizeLabel || ''
        };
      });

      const status = saveStatus || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        partyType: 'vendor',
        partyId: formData.partyId,
        partyName: formData.partyName,
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        invoiceNo: formData.invoiceNo,
        vehicleNo: formData.vehicleNo,
        driverName: formData.driverName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        notes: formData.notes,
        lines: calculatedLines,
        totalReceived: totals.totalReceived,
        totalAccepted: totals.totalReceived, // receivedQty = acceptedQty
        subtotal: totals.totalAmount,
        totalAmount: totals.totalAmount,
        status,
        ...(status === 'Received' ? { receivedBy: currentUser.uid, receivedAt: new Date().toISOString() } : {})
      };

      let savedTransaction;

      if (editingGRN) {
        // Update existing
        savedTransaction = await updateTransaction(
          activeCompany.id,
          editingGRN.id,
          transactionData,
          currentUser.uid
        );

        // Handle status change
        if (status !== editingGRN.status) {
          await updateTransactionStatus(activeCompany.id, editingGRN.id, status, currentUser.uid);

          // Generate stock flow on Received status
          // updateTransactionStatus already sets receivedBy/receivedAt for 'Received' status.
        }

        savedTransaction = { ...savedTransaction, status };
      } else {
        // Create new
        savedTransaction = await createTransaction(
          activeCompany.id,
          transactionData,
          currentUser.uid
        );

        // If saving as Received, update status
        if (status === 'Received') {
          await updateTransactionStatus(activeCompany.id, savedTransaction.id, status, currentUser.uid);
          // receivedBy/receivedAt already included in transactionData above
        }
      }

      // Update the linked PO's received quantities + status (FLW-02). An edit /
      // re-save first takes this GRN's previous quantities off, then adds the new
      // ones, so the PO never counts the same receipt twice.
      const oldPOId = (editingGRN && editingGRN.parentId && GRN_COUNTS_ON_PO.includes(editingGRN.status)) ? editingGRN.parentId : null;
      const newPOId = (formData.parentId && GRN_COUNTS_ON_PO.includes(status)) ? formData.parentId : null;
      if (oldPOId || newPOId) {
        try {
          const takeOff = oldPOId ? { entries: grnPOEntries(editingGRN.lines), sign: -1 } : null;
          const addOn = newPOId ? { entries: grnPOEntries(calculatedLines), sign: 1 } : null;
          if (oldPOId && oldPOId === newPOId) {
            await applyPOProgress(activeCompany.id, newPOId, [takeOff, addOn], currentUser.uid);
          } else {
            if (oldPOId) await applyPOProgress(activeCompany.id, oldPOId, [takeOff], currentUser.uid);
            if (newPOId) await applyPOProgress(activeCompany.id, newPOId, [addOn], currentUser.uid);
          }
        } catch (err) { console.error('Error updating PO:', err); }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editingGRN ? editingGRN.id : savedTransaction?.id;
      const savedRecord = editingGRN
        ? { ...editingGRN, ...transactionData, id: savedId, status }
        : { ...savedTransaction, status };
      applyLocal(prev => editingGRN
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // D3: this save changed the linked PO's received/pending quantities, so the
      // cached PO link-data is stale — mark it for a refetch on the NEXT form
      // open (zero reads now) instead of re-scanning every PO here.
      formDataLoadedRef.current = false;

      // Background refresh — reconciles with server data without blocking UI.
      loadGRNs().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving GRN:', error);
      alert('Error saving GRN: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (grn) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (grn.childIds && grn.childIds.length > 0) { alert('Cannot delete GRN with linked documents'); return; }
    if (!window.confirm(`Delete ${grn.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, grn.id);
      // FLW-02: a deleted GRN that counted on its PO gives its quantities back.
      if (grn.parentId && GRN_COUNTS_ON_PO.includes(grn.status)) {
        try {
          await applyPOProgress(activeCompany.id, grn.parentId, [{ entries: grnPOEntries(grn.lines), sign: -1 }], currentUser.uid);
        } catch (err) { console.error('Error reversing PO receipt:', err); }
      }
      // D3: PO received quantities may have changed — mark the form link-data
      // stale so the NEXT form open refetches it.
      formDataLoadedRef.current = false;
      await loadGRNs();
    } catch (error) {
      console.error('Error deleting GRN:', error);
      alert('Error deleting GRN: ' + error.message);
    }
  };

  // Print
  const handlePrint = (grn) => {
    printDocument('grn', grn || viewingGRN, activeCompany, printConfig, activeBranch);
  };

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by GRN#, vendor, PO#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Received">Received</option>
            <option value="Invoiced">Invoiced</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New GRN</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading GRNs...</LoadingMessage>
      ) : filteredGRNs.length === 0 ? (
        <TranEmptyState>
          <FiPackage size={48} />
          <h3>No Goods Receipts</h3>
          <p>{grns.length === 0 ? 'Create your first goods receipt note' : 'No GRNs match your filters'}</p>
          {grns.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create GRN</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">GRN Number</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Vendor</SortableTh>
                <SortableTh field="parentDocNumber">PO Ref</SortableTh>
                <SortableTh field="locationName">Location</SortableTh>
                <SortableTh field="totalReceived" style={{ textAlign: 'right' }}>Received Qty</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredGRNs.map(grn => (
                <Tr key={grn.id}>
                  <Td>
                    <strong>{grn.docNumber}</strong>
                    {grn.childIds?.length > 0 && (
                      <LinkedBadge>
                        <FiLink size={12} /> <LinkedDocNumbers ids={grn.childIds} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
                      </LinkedBadge>
                    )}
                  </Td>
                  <Td>{grn.docDate}</Td>
                  {showBranchCol && <Td>{grn.branchName || '-'}</Td>}
                  <Td>{grn.partyName}</Td>
                  <Td><ParentDocLink parentDocNumber={grn.parentDocNumber} parentTranType={grn.parentTranType || 'PO'} parentId={grn.parentId} /></Td>
                  <Td>{grn.locationName || '-'}</Td>
                  <Td style={{ textAlign: 'right' }}>{grn.totalReceived || 0}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(grn.totalAmount)}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[grn.status]}>{grn.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(grn)}><FiEye /></IconBtn>
                      {canChange && <IconBtn title="Edit" onClick={() => handleEdit(grn)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(grn)}><FiPrinter /></IconBtn>
                      {grn.status !== 'Draft' && grn.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="grn" doc={grn} party={vendors.find(v => v.id === grn.partyId)} variant="icon" />
                      )}
                      {canDelete && (!grn.childIds || grn.childIds.length === 0) && (
                        <IconBtn title="Delete" $danger onClick={() => handleDelete(grn)}><FiTrash2 /></IconBtn>
                      )}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredGRNs.length : pagination.totalCount}</strong> receipt(s)</span>
        <span>
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Received: <strong>{listSummary.counts.Received || 0}</strong> |{' '}
          Invoiced: <strong>{listSummary.counts.Invoiced || 0}</strong> |{' '}
          Cancelled: <strong>{listSummary.counts.Cancelled || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingGRN) return null;
    const g = viewingGRN;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingGRN(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {g.docNumber}
            <StatusBadge $color={STATUS_COLORS[g.status]} style={{ marginLeft: 12 }}>{g.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(g)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(g)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Receipt Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>GRN Number</label><span>{g.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{g.docDate}</span></InfoItem>
            <InfoItem><label>Vendor</label><span>{g.partyName}</span></InfoItem>
            <InfoItem><label>PO Reference</label><span>{g.parentDocNumber || 'Direct GRN'}</span></InfoItem>
            {g.childIds?.length > 0 && (
              <InfoItem><label>Linked Documents</label><span><LinkedDocNumbers ids={g.childIds} max={0} showIcon={false} /></span></InfoItem>
            )}
            <InfoItem><label>Location</label><span>{g.locationName || '-'}</span></InfoItem>
            <InfoItem><label>Vendor Invoice</label><span>{g.invoiceNo || '-'}</span></InfoItem>
            <InfoItem><label>Vehicle No.</label><span>{g.vehicleNo || '-'}</span></InfoItem>
            <InfoItem><label>Driver</label><span>{g.driverName || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{g.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{g.jobName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Ordered</Th>
                  <Th style={{ textAlign: 'right' }}>Received</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Cost</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(g.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.orderedQty || '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.receivedQty} {line.unitOfMeasure && <span style={{ fontSize: '11px', color: '#64748B' }}>{line.unitOfMeasure}</span>}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 500 }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Total Received</td><td>{g.totalReceived}</td></tr>
                <TotalRow><td>Total Amount</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(g.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        {g.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{g.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={g?.id} attachment={g?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => {
    // Only this branch's orders (see utils/branchLink.js).
    const vendorPOs = approvedPOs.filter(po => !formData.partyId || po.partyId === formData.partyId)
      .filter(po => inActiveBranch(po, activeBranch, formData.parentId));

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingGRN ? `Edit: ${editingGRN.docNumber}` : 'New Goods Receipt Note'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && (formData.status === 'Draft' || !editingGRN) && (
              <SaveButton $approve onClick={() => handleSave('Received')} disabled={saving}>
                <FiCheck /> Save & Receive
              </SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Receipt Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>GRN Number</Label>
              <Input value={editingGRN?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>GRN Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Link to PO</Label>
              <Select value={formData.parentId} onChange={e => handlePOChange(e.target.value)}>
                <option value="">-- Direct GRN (No PO) --</option>
                {vendorPOs.map(po => (
                  <option key={po.id} value={po.id}>{po.docNumber} - {po.partyName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup $span2>
              <Label>Vendor *</Label>
              <VendorSelect vendors={vendors} selectedVendorId={formData.partyId} onChange={handleVendorChange} disabled={!!formData.parentId} balanceOnDemand />
            </FormGroup>
            <FormGroup>
              <Label>Vendor Invoice No.</Label>
              <Input value={formData.invoiceNo} onChange={e => handleHeaderChange('invoiceNo', e.target.value)} placeholder="Vendor invoice #" />
            </FormGroup>
            <FormGroup>
              <Label>Receiving Location {locations.length > 0 ? '*' : ''}</Label>
              <Select value={formData.locationId} onChange={e => handleLocationChange(e.target.value)}>
                <option value="">-- Select Location --</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.locationName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Vehicle No.</Label>
              <Input value={formData.vehicleNo} onChange={e => handleHeaderChange('vehicleNo', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Driver Name</Label>
              <Input value={formData.driverName} onChange={e => handleHeaderChange('driverName', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
            {!formData.parentId && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Item</LineTh>
                  <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Description</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Ordered</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Received</LineTh>
                  <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Unit Cost</LineTh>
                  <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Batch #</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Expiry Date</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      <LineTd>
                        <ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} disabled={!!formData.parentId} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} />
                      </LineTd>
                      <LineTd>
                        <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description" disabled={!!formData.parentId} />
                        
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" value={line.orderedQty} disabled style={{ textAlign: 'right', background: colors.neutral[50] }} />
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" value={line.receivedQty}
                            onChange={e => handleLineChange(index, 'receivedQty', Number(e.target.value))}
                            disabled={line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined }} />
                          <LineUnitPicker
                            item={items.find(i => i.id === line.itemId)}
                            line={line}
                            onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                          />
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.receivedQty} hasSize={line.hasSize}
                          onChange={(q) => handleLineChange(index, 'receivedQty', q)} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitCost}
                          onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          style={{ textAlign: 'right' }} />
                      </LineTd>
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>
                          {line.trackingType === 'Batch' ? (
                            <LineInput value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} placeholder="Batch #" />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>
                          {line.trackingType === 'Batch' ? (
                            <LineInput type="date" value={line.expiryDate || ''} onChange={e => handleLineChange(index, 'expiryDate', e.target.value)} />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>
                          {line.trackingType === 'Serial' ? (
                            <LineInput value={line.serialText !== undefined ? line.serialText : (line.serialNumbers || []).join(', ')}
                              onChange={e => handleLineChange(index, 'serialText', e.target.value)}
                              onBlur={e => { const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean); handleLineChange(index, 'serialNumbers', parsed); handleLineChange(index, 'serialText', undefined); }}
                              placeholder={`Enter ${line.receivedQty || 0} serial(s), comma-separated`} />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!formData.parentId && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={!!formData.parentId} leftColSpan={2} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Total Received</td><td>{totals.totalReceived}</td></tr>
                <TotalRow><td>Total Amount</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Notes..." rows={3} />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingGRN?.id}
          attachment={editingGRN?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingGRN(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    // Same as the form buttons: a new / draft GRN is received; an already
    // received or invoiced GRN is re-saved in its current status.
    onSave: canEdit ? () => handleSave(editingGRN && formData.status && formData.status !== 'Draft' ? formData.status : 'Received') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Goods Receipt Note (GRN)" breadcrumb="Purchases / GRN">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS (unique to this file) ============

const LinkedBadge = styled.span`
  display: inline-flex; align-items: center; gap: 2px;
  margin-left: 8px; padding: 2px 6px;
  background: ${colors.info.main}15; color: ${colors.info.main};
  border-radius: 4px; font-size: 10px; font-weight: 600;
`;

export default GRN;
